import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Modal,
  Progress,
  Radio,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ArrowRightOutlined, WarningOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  CONFIDENCE_CERTAIN,
  CONFIDENCE_LIKELY,
  CONFIDENCE_ORDER,
  CONFIDENCE_UNSURE,
  defaultSelectedKeys,
  isSelectableProposal,
  linkEntriesForProposals,
  proposalsWithConfidence,
} from "functions/productLinkPlan";
import { SHOP_COLORS, shopLabel } from "./constants";

const { Text, Paragraph } = Typography;

const FILTER_ALL = "all";
const FILTER_CONFLICTS = "conflicts";

const CONFIDENCE_COLORS = {
  [CONFIDENCE_CERTAIN]: "green",
  [CONFIDENCE_LIKELY]: "blue",
  [CONFIDENCE_UNSURE]: "orange",
};

/** Above this share of one match level, "the ids are the same" is a statement, not a tendency. */
const DOMINANT_SHARE = 0.8;

const percent = (share) => Math.round((share || 0) * 100);

const ProductCell = ({ side }) => (
  <Space size={6}>
    <Tag color={SHOP_COLORS[side.shopSource] || "default"}>{side.shopSource}</Tag>
    <span>{side.name || side.productId}</span>
  </Space>
);

/**
 * The first linking round, in one screen.
 *
 * Linking 250-odd products one modal at a time was the known limitation of phase 1. This is the
 * bulk version - and it is deliberately not a "koppel alles"-button: it shows *why* each pair is
 * proposed and how sure that is, because a wrong link does not fail loudly, it puts one
 * product's pick weight on a different piece of meat and the picker finds out in the freezer.
 *
 * The summary at the top is also the measurement nobody has ever done: if nearly every proposal
 * comes out on "Zelfde product-id", HVO's product collection really is a copy of VCE's and this
 * round is a formality. If none do, the ids were regenerated and every proposal below is a
 * statement about names - which is a very different thing to be ticking 250 boxes on.
 */
const LinkProposalModal = ({
  open,
  proposal,
  loading,
  creating,
  progress,
  result,
  canLink,
  onCreate,
  onClose,
}) => {
  const { t } = useTranslation();
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [filter, setFilter] = useState(FILTER_ALL);

  const proposals = useMemo(() => (proposal && proposal.proposals) || [], [proposal]);
  const unmatched = (proposal && proposal.unmatched) || [];
  const summary = (proposal && proposal.summary) || null;

  // A fresh proposal starts on its own defaults: zeker and waarschijnlijk on, onzeker and every
  // conflict off. Once per opening, though, not once per proposal: the proposal is now computed
  // after the modal is on screen (so it can show that it is working) and recomputed whenever
  // the products or links revalidate underneath. Seeding on every one of those would throw away
  // the work of someone halfway through 250 rows, and seeding only on `open` would never seed
  // at all, because at that moment there is no proposal yet. Hence the flag: the first proposal
  // of this opening sets the defaults, every later one leaves the ticks alone.
  const seeded = useRef(false);

  useEffect(() => {
    if (!open) {
      seeded.current = false;
      return;
    }
    if (seeded.current || !proposal) return;
    seeded.current = true;
    setSelectedKeys(defaultSelectedKeys(proposal.proposals || []));
    setFilter(FILTER_ALL);
  }, [open, proposal]);

  // A recomputation can make a row disappear - its counterpart got linked by someone else, or
  // the product itself did - and its key carries both sides, so a vanished key can never be
  // handed to a different pair. It can only linger: `preserveSelectedRowKeys` keeps keys that
  // are off-screen, which is what makes filtering safe, and it cannot tell "filtered away"
  // from "gone". So the keys that no longer exist are dropped here, against the whole
  // proposal rather than the filtered view. The same array is returned when nothing changed,
  // or this would re-render on every revalidation for nothing.
  useEffect(() => {
    if (!open) return;
    setSelectedKeys((keys) => {
      const alive = new Set(proposals.map((entry) => entry.key));
      const kept = keys.filter((key) => alive.has(key));
      return kept.length === keys.length ? keys : kept;
    });
  }, [open, proposals]);

  const visibleProposals = useMemo(() => {
    if (filter === FILTER_ALL) return proposals;
    if (filter === FILTER_CONFLICTS) return proposals.filter((entry) => entry.conflict);
    // Conflicts get their own tab rather than sitting inside a confidence tab: they cannot be
    // ticked, so listing them under "Zeker" would suggest they are part of that one click.
    return proposalsWithConfidence(proposals, filter).filter((entry) => !entry.conflict);
  }, [proposals, filter]);

  /**
   * What the button will actually write - not what is ticked.
   *
   * The two differ: a ticked key can point at a proposal that `linkEntriesForProposals` drops
   * (a conflict, or a pair missing one of the two shops), and it can point at nothing at all
   * once the proposal has been recomputed. Counting ticks would then promise "185
   * koppelingen" and deliver 181, which is exactly the kind of quiet difference this screen
   * exists to avoid. So the count comes out of the same filter the write does.
   */
  const createEntries = useMemo(() => {
    const chosen = new Set(selectedKeys);
    return linkEntriesForProposals(proposals.filter((entry) => chosen.has(entry.key)));
  }, [proposals, selectedKeys]);

  const createCount = createEntries.length;

  const selectOnly = (predicate) =>
    setSelectedKeys(proposals.filter((entry) => isSelectableProposal(entry) && predicate(entry)).map((entry) => entry.key));

  const columns = [
    {
      title: t("product_data.proposal_source", { shop: shopLabel(proposal && proposal.sourceShop) }),
      dataIndex: "source",
      render: (source) => <ProductCell side={source} />,
    },
    {
      title: "",
      dataIndex: "arrow",
      width: 32,
      render: () => <ArrowRightOutlined style={{ color: "#a0a0a0" }} />,
    },
    {
      title: t("product_data.proposal_target", { shop: shopLabel(proposal && proposal.targetShop) }),
      dataIndex: "target",
      render: (target) => <ProductCell side={target} />,
    },
    {
      title: t("product_data.proposal_reason"),
      dataIndex: "reasonLabel",
      width: 220,
      render: (reasonLabel, entry) => (
        <Tag color={entry.reason === "id" ? "purple" : entry.reason === "pickRoute" ? "blue" : "default"}>
          {reasonLabel}
        </Tag>
      ),
    },
    {
      title: t("product_data.proposal_confidence"),
      dataIndex: "confidence",
      width: 130,
      render: (confidence) => (
        <Tag color={CONFIDENCE_COLORS[confidence] || "default"}>
          {t(`product_data.confidence_${confidence}`)}
        </Tag>
      ),
    },
    {
      title: t("product_data.proposal_signals"),
      dataIndex: "signals",
      width: 260,
      render: (_, entry) => (
        <Space size={4} wrap>
          {!!entry.conflict && (
            <Tooltip title={t("product_data.proposal_conflict_tooltip", { others: entry.conflictWith.join(", ") })}>
              <Tag color="red" icon={<WarningOutlined />}>
                {t("product_data.proposal_conflict")}
              </Tag>
            </Tooltip>
          )}
          {!!entry.nameMismatch && (
            <Tooltip title={t("product_data.proposal_name_mismatch_tooltip")}>
              <Tag color="orange">{t("product_data.proposal_name_mismatch")}</Tag>
            </Tooltip>
          )}
          {entry.alternatives > 0 && (
            <Tooltip title={t("product_data.proposal_alternatives_tooltip")}>
              <Tag>{t("product_data.proposal_alternatives", { amount: entry.alternatives })}</Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  // --- the headline: are the document ids the same, yes or no? -----------------------------
  const headline = () => {
    if (!summary || summary.proposed === 0) return null;

    const idCount = summary.byReason.id || 0;
    if (idCount === 0) {
      return (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message={t("product_data.proposal_no_id_title")}
          description={t("product_data.proposal_no_id_body", { total: summary.proposed })}
        />
      );
    }

    if (summary.topReason === "id" && summary.topReasonShare >= DOMINANT_SHARE) {
      return (
        <Alert
          type="success"
          showIcon
          className="mb-3"
          message={t("product_data.proposal_id_dominant_title")}
          description={t("product_data.proposal_id_dominant_body", {
            matched: idCount,
            total: summary.proposed,
            percentage: percent(summary.topReasonShare),
          })}
        />
      );
    }

    return (
      <Alert
        type="info"
        showIcon
        className="mb-3"
        message={t("product_data.proposal_mixed_title", {
          level: t(`product_data.reason_${summary.topReason}`),
        })}
        description={t("product_data.proposal_mixed_body", {
          level: t(`product_data.reason_${summary.topReason}`),
          matched: summary.topReasonCount,
          total: summary.proposed,
          ids: idCount,
        })}
      />
    );
  };

  // --- what happened after the writes ------------------------------------------------------
  const resultView = () => {
    if (!result) return null;

    const skipped = (result.results || []).filter((entry) => !entry.ok);
    const byReason = skipped.reduce((groups, entry) => {
      const reason = entry.reason || "unknown";
      return { ...groups, [reason]: [...(groups[reason] || []), entry] };
    }, {});

    return (
      <>
        <Alert
          type={skipped.length === 0 ? "success" : "warning"}
          showIcon
          className="mb-3"
          message={t("product_data.proposal_result_title", {
            created: result.created,
            skipped: result.skipped,
          })}
          description={
            skipped.length === 0
              ? t("product_data.proposal_result_all_created")
              : t("product_data.proposal_result_has_skipped")
          }
        />

        {skipped.length > 0 && (
          <Collapse
            defaultActiveKey={["skipped"]}
            items={[
              {
                key: "skipped",
                label: t("product_data.proposal_result_skipped_label", { amount: skipped.length }),
                children: (
                  <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    {Object.keys(byReason).map((reason) => (
                      <div key={reason}>
                        <Text strong>{t(`product_data.skip_reason_${reason}`)}</Text>
                        <ul style={{ margin: "4px 0 0 18px" }}>
                          {byReason[reason].map((entry) => (
                            <li key={`${entry.vceProductId}|${entry.hvoProductId}|${entry.index}`}>
                              {entry.vceName || entry.vceProductId} ↔ {entry.hvoName || entry.hvoProductId}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </>
    );
  };

  const footer = !!result
    ? [
        <Button key="close" type="primary" onClick={onClose}>
          {t("close")}
        </Button>,
      ]
    : [
        <Button key="cancel" onClick={onClose} disabled={creating}>
          {t("cancel")}
        </Button>,
        <Button
          key="create"
          type="primary"
          loading={creating}
          disabled={!canLink || loading || createCount === 0}
          onClick={() => onCreate(selectedKeys)}
        >
          {t("product_data.proposal_create", { amount: createCount })}
        </Button>,
      ];

  return (
    <Modal
      open={open}
      width={1100}
      onCancel={creating ? undefined : onClose}
      maskClosable={!creating}
      closable={!creating}
      title={t("product_data.proposal_title")}
      footer={footer}
    >
      {!!result ? (
        resultView()
      ) : !summary ? (
        // Walking two catalogues against each other takes a moment on a thousand products
        // apiece, and a modal that opens on an empty box looks like a screen that has hung.
        <div style={{ textAlign: "center", padding: "56px 0" }}>
          {!!loading ? (
            <Space direction="vertical" size={12}>
              <Spin size="large" />
              <Text type="secondary">{t("product_data.proposal_computing")}</Text>
            </Space>
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("product_data.proposal_empty")} />
          )}
        </div>
      ) : (
        <>
          <Paragraph style={{ color: "#6b6b6b" }}>
            {t("product_data.proposal_counts", {
              vceTotal: (summary.shops.VCE || {}).total || 0,
              hvoTotal: (summary.shops.HVO || {}).total || 0,
              linked: summary.existingLinks,
              considered: summary.considered,
              proposed: summary.proposed,
              unmatched: summary.unmatched,
            })}
          </Paragraph>

          {headline()}

          {summary.conflicts > 0 && (
            <Alert
              type="error"
              showIcon
              className="mb-3"
              message={t("product_data.proposal_conflicts_title", {
                amount: summary.conflicts,
                targets: summary.conflictTargets,
              })}
              description={t("product_data.proposal_conflicts_body")}
            />
          )}

          {summary.nameMismatches > 0 && (
            <Alert
              type="warning"
              showIcon
              className="mb-3"
              message={t("product_data.proposal_mismatch_title", { amount: summary.nameMismatches })}
              description={t("product_data.proposal_mismatch_body")}
            />
          )}

          {!canLink && (
            <Alert type="error" showIcon className="mb-3" message={t("product_data.links_unavailable")} />
          )}

          {/* A recomputation keeps the previous proposal on screen - someone may be halfway
              through 250 rows - so this is a note next to it, not a curtain over it. */}
          {!!loading && (
            <Space size={8} className="mb-3">
              <Spin size="small" />
              <Text type="secondary">{t("product_data.proposal_recomputing")}</Text>
            </Space>
          )}

          <Space wrap className="mb-3" style={{ width: "100%", justifyContent: "space-between" }}>
            <Radio.Group value={filter} onChange={(event) => setFilter(event.target.value)}>
              <Radio.Button value={FILTER_ALL}>
                {t("product_data.proposal_filter_all", { amount: proposals.length })}
              </Radio.Button>
              {CONFIDENCE_ORDER.map((confidence) => (
                <Radio.Button key={confidence} value={confidence}>
                  {t(`product_data.confidence_${confidence}`)} ({summary.byConfidence[confidence] || 0})
                </Radio.Button>
              ))}
              <Radio.Button value={FILTER_CONFLICTS} disabled={summary.conflicts === 0}>
                {t("product_data.proposal_filter_conflicts", { amount: summary.conflicts })}
              </Radio.Button>
            </Radio.Group>

            <Space wrap>
              <Button size="small" onClick={() => selectOnly((entry) => entry.confidence === CONFIDENCE_CERTAIN)}>
                {t("product_data.proposal_select_certain")}
              </Button>
              <Button
                size="small"
                onClick={() =>
                  selectOnly((entry) => entry.confidence !== CONFIDENCE_UNSURE)
                }
              >
                {t("product_data.proposal_select_default")}
              </Button>
              <Button size="small" onClick={() => setSelectedKeys([])}>
                {t("product_data.proposal_select_none")}
              </Button>
            </Space>
          </Space>

          <div className="table-responsive">
            <Table
              columns={columns}
              dataSource={visibleProposals}
              rowKey="key"
              size="small"
              pagination={{ defaultPageSize: 25, showSizeChanger: true }}
              rowSelection={{
                selectedRowKeys: selectedKeys,
                // Selection is kept for the whole proposal, not per page: switching the filter
                // must not silently untick what is no longer on screen.
                preserveSelectedRowKeys: true,
                onChange: setSelectedKeys,
                getCheckboxProps: (entry) => ({
                  disabled: !isSelectableProposal(entry) || creating,
                }),
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t("product_data.proposal_no_rows")}
                  />
                ),
              }}
            />
          </div>

          {/* Never hidden: these are exactly the products that still need someone. */}
          <Collapse
            className="mt-3"
            items={[
              {
                key: "unmatched",
                label: t("product_data.proposal_unmatched_label", { amount: unmatched.length }),
                children:
                  unmatched.length === 0 ? (
                    <Text type="secondary">{t("product_data.proposal_unmatched_none")}</Text>
                  ) : (
                    <>
                      <Paragraph style={{ color: "#6b6b6b" }}>
                        {t("product_data.proposal_unmatched_body")}
                      </Paragraph>
                      <Space size={[6, 6]} wrap>
                        {unmatched.map((entry) => (
                          <Tag key={`${entry.shopSource}|${entry.productId}`}>
                            {entry.name || entry.productId}
                          </Tag>
                        ))}
                      </Space>
                    </>
                  ),
              },
            ]}
          />

          {!!creating && (
            <div className="mt-3">
              <Progress
                percent={
                  !progress || !progress.total
                    ? 0
                    : Math.round((progress.committed / progress.total) * 100)
                }
                status="active"
              />
              <Text type="secondary">
                {t("product_data.proposal_progress", {
                  done: (progress && progress.committed) || 0,
                  total: (progress && progress.total) || createCount,
                })}
              </Text>
            </div>
          )}
        </>
      )}
    </Modal>
  );
};

export default LinkProposalModal;
